import { useSelector } from 'react-redux';
import { ticApi } from 'redux/modules/tic/api-slice/tic.apiSlice';
import { getLastRelevantMemoryEndingForAiStatus } from 'redux/modules/tic/tic.memories.selectors';
import { getCurrentAccountId } from 'redux/selectors/accounts';
import { getDerivedDatesInfo } from 'redux/selectors/calendar';
import { getUserId } from 'redux/selectors/user';
import { getPredictionQuery } from '~domains/ai/predictions/redux/predictions.selectors';
import { getIsTicAiFeatureAvailable } from '~domains/ai/redux/ai.availability.selectors';
import useTicAiPusherManager from './useTicAiPusherManager';

/**
 * Subscribes to the relevant  AI/predictions data.
 */
type Params = {
  /** Ref RTKQ, if you want to skip all XHR calls */
  skip?: boolean;
};
const useTicAiDataSubscriber = (params?: Params) => {
  const isFeatureAvailable = useSelector(getIsTicAiFeatureAvailable);
  const predictionQuery = useSelector(getPredictionQuery);
  const timely_account_id = useSelector(getCurrentAccountId);
  const timely_user_id = useSelector(getUserId);
  const dateMeta = useSelector(getDerivedDatesInfo);
  const hasMemories = !!useSelector(getLastRelevantMemoryEndingForAiStatus);

  // skip (aka don't load data) if TIC API or feature is unavailable.
  const skip = params?.skip || !isFeatureAvailable;

  // Fetch AI preferences (we'll read these in "get predictions" selectors++)
  const { data: preferences } = ticApi.useGetAiPreferencesQuery(undefined, { skip });

  // Fetch workspace-specific timesheet assistant preferences
  ticApi.useGetAiWorkspacePreferencesQuery({ timelyAccountId: timely_account_id }, { skip });

  // skip loading AI data if AI is disabled

  const skipWithPreferences = skip || !preferences?.enabled || !hasMemories;

  // IF enabled: Load AI Engine & Day Statuses
  ticApi.useGetAiPredictionStatusForDateRangeQuery(
    {
      fromDate: dateMeta.since,
      toDate: dateMeta.upto,
      timelyUserId: timely_user_id,
      timelyAccountId: timely_account_id,
    },
    {
      skip: skipWithPreferences,
      refetchOnMountOrArgChange: 0, // Force fetch status every time, ignore any cache that may exist
    }
  );

  // IF enabled: Fetch AI predictions (when enabled)
  ticApi.useGetAiPredictionsInDateRangeQuery(predictionQuery, {
    skip: skipWithPreferences,
  });

  // Subscribe to AI pusher signals, and get data back via selectors
  // If AI is disabled, we'll skip this hook entirely
  useTicAiPusherManager({ skip: skipWithPreferences });
};

export default useTicAiDataSubscriber;
